import React from "react";
import { useQuery } from "@tanstack/react-query";
import { CrudSection } from "components/admin/workspace-crud";
import {
  buildEventCreateValues,
  buildEventFields,
  buildEventUpdateValues,
} from "pages/app/workspace/sections/forms/athlete-section-forms";
import { workspaceApi } from "pages/app/workspace/services";
import { formatDate } from "utils/admin";
import { canManageOwnedAthleteEvents, hasLinkedPlayerProfile } from "utils/portal-access";

export const EventsSection: React.FC<{ currentUser: any; portalAccess: any }> = ({
  currentUser,
  portalAccess,
}) => {
  const { data: athletes = [] } = useQuery({
    queryKey: ["workspace-events-athletes"],
    queryFn: () => workspaceApi.athletes.list(),
  });
  const eventsQuery = useQuery({
    queryKey: ["workspace-events", currentUser?.player?.id || "all"],
    queryFn: () =>
      workspaceApi.events.list({
        page: 1,
        per_page: 50,
        player_id:
          hasLinkedPlayerProfile(currentUser) && currentUser?.role !== "admin"
            ? currentUser.player.id
            : undefined,
      }),
  });

  const defaultPlayerId = currentUser?.player?.id || athletes[0]?.id || "";
  const canManageEvents = canManageOwnedAthleteEvents(currentUser, portalAccess);
  const visibleAthletes =
    hasLinkedPlayerProfile(currentUser) && currentUser?.role !== "admin"
      ? athletes.filter((athlete: any) => athlete.id === currentUser?.player?.id)
      : athletes;
  const eventFields = buildEventFields(visibleAthletes);

  return (
    <CrudSection
      buildCreateValues={() => buildEventCreateValues(defaultPlayerId)}
      buildUpdateValues={(row) => buildEventUpdateValues(currentUser, row)}
      canCreate={canManageEvents && Boolean(defaultPlayerId)}
      canEdit={canManageEvents}
      canRemove={canManageEvents}
      columns={[
        { label: "Event", accessor: (row) => row.name || "Untitled event" },
        {
          label: "Athlete",
          accessor: (row) => row.player_name || row.player?.full_name || "Unknown",
        },
        { label: "Date", accessor: (row) => formatDate(row.date) },
        { label: "Location", accessor: (row) => row.location || "TBC" },
      ]}
      createLabel="Add event"
      data={eventsQuery.data || []}
      description="Create and maintain event records linked to athletes. Player accounts stay scoped to their own profile, while admins can manage any athlete."
      emptyDescription="No events are available yet."
      fields={eventFields}
      invalidateKeys={[["workspace-events"], ["athlete", currentUser?.player?.id]]}
      isLoading={eventsQuery.isLoading}
      onCreate={(payload) => workspaceApi.events.create(payload)}
      onRemove={(row) => workspaceApi.events.remove(row.id)}
      onUpdate={(row, payload) => workspaceApi.events.update(row.id, payload)}
      title="Events"
    />
  );
};
